import * as Phaser from "phaser";
import {KitchenState} from "../model/state";
import {Order} from "../model/order";
import {IngredientTypes} from "../model/ingredients";
import {StationType} from "../model/station";
import {RECIPES, RecipeStep as RegistryRecipeStep} from "../model/recipe_registry";
import {CANVAS_WIDTH, HUD_HEIGHT} from "./config";

// Layout constants
const CARD_WIDTH = 120;
const CARD_HEIGHT = 44;
const CARD_MARGIN = 8;
const CARD_START_X = 10;
const CARD_START_Y = 28;
const CARD_PADDING = 6;
const CARD_BG_COLOR = 0x222244;
const CARD_BORDER_COLOR = 0x4444AA;
const CARD_BORDER_WIDTH = 2;

// Timer bar
const TIMER_BAR_HEIGHT = 6;
const TIMER_BAR_Y_OFFSET = CARD_HEIGHT - TIMER_BAR_HEIGHT - 4;

// Color urgency thresholds (fraction of time remaining)
const URGENCY_CRITICAL = 0.10; // flashing red below this threshold
const URGENCY_HIGH = 0.25;
const URGENCY_MEDIUM = 0.50;

// Flash frequency for critical urgency (ms between visibility toggles)
const FLASH_INTERVAL_MS = 300;

// Score/timer HUD
const SCORE_FONT_SIZE = '18px';
const TIMER_FONT_SIZE = '18px';
const HUD_DEPTH = 100;
const SEPARATOR_COLOR = 0x444466;

// Order timeout for urgency calculation (should match server)
const ORDER_TIMEOUT_MS = 60000;

interface RecipeStep {
    ingredient: IngredientTypes | null;
    station: StationType | null;
    label: string;
}

// Width available for recipe step text: card inner width (pixels), used for fixedWidth text style.
// Constrains step label to a single line without word-wrap to prevent overflow beyond the HUD band.
export const STEP_LABELS_FIXED_WIDTH = CARD_WIDTH - CARD_PADDING * 2;

// Derive display steps and dish names from the recipe registry (single source of truth).
// This ensures all 5 recipes are reflected in the HUD without manual maintenance.
export function buildRecipeSteps(step: RegistryRecipeStep): RecipeStep {
    switch (step.action) {
        case 'chop':
            return {ingredient: step.inputs[0], station: step.station ?? null, label: `Chop ${step.inputs[0].replace(/_/g, ' ')}`};
        case 'cook':
            return {ingredient: step.inputs[0], station: step.station ?? null, label: `Cook ${step.inputs[0].replace(/_/g, ' ')}`};
        case 'merge':
            return {ingredient: null, station: null, label: `Combine ${step.inputs.map(i => i.replace(/_/g, ' ')).join(' + ')}`};
    }
}

// Joins step labels into a single-line string with " > " separators.
export function buildStepLabelsString(steps: RecipeStep[]): string {
    return steps.map(s => s.label).join(' > ');
}

const RECIPE_STEPS: Map<IngredientTypes, RecipeStep[]> = new Map(
    RECIPES.map(recipe => [recipe.dishType, recipe.steps.map(buildRecipeSteps)])
);

const DISH_NAMES: Record<string, string> = Object.fromEntries(
    RECIPES.map(recipe => [recipe.dishType, recipe.displayName])
);

export class OrdersHUD {
    private scene: Phaser.Scene;
    private container: Phaser.GameObjects.Container;
    private scoreText: Phaser.GameObjects.Text;
    private timerText: Phaser.GameObjects.Text;
    private cardContainers: Map<string, Phaser.GameObjects.Container> = new Map();

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
        this.container = scene.add.container(0, 0).setDepth(HUD_DEPTH);

        this.scoreText = scene.add.text(10, 8, 'Score: 0', {
            fontSize: SCORE_FONT_SIZE, color: '#fff', fontStyle: 'bold'
        });
        this.container.add(this.scoreText);

        this.timerText = scene.add.text(CANVAS_WIDTH - 120, 8, 'Ready', {
            fontSize: TIMER_FONT_SIZE, color: '#fff', fontStyle: 'bold'
        });
        this.container.add(this.timerText);

        // Separator line between HUD band and game area
        const separator = scene.add.graphics();
        separator.lineStyle(1, SEPARATOR_COLOR, 0.8);
        separator.lineBetween(0, HUD_HEIGHT - 1, CANVAS_WIDTH, HUD_HEIGHT - 1);
        this.container.add(separator);
    }

    update(state: KitchenState): void {
        if (!state) return;

        this.updateScore(state.score);
        this.updateTimer(state);
        this.updateOrderCards(state);
    }

    private updateScore(score: number): void {
        this.scoreText.setText(`Score: ${score}`);
    }

    private updateTimer(state: KitchenState): void {
        if (state.roundActive) {
            const secs = Math.ceil(state.roundTimeRemaining / 1000);
            const minutes = Math.floor(secs / 60);
            const seconds = (secs % 60).toString().padStart(2, '0');
            this.timerText.setText(`${minutes}:${seconds}`);

            // Urgency coloring on the round timer
            const fraction = state.roundTimeRemaining / 180000; // 3 min round
            if (fraction <= URGENCY_HIGH) {
                this.timerText.setColor('#ff3333');
            } else if (fraction <= URGENCY_MEDIUM) {
                this.timerText.setColor('#ffaa00');
            } else {
                this.timerText.setColor('#ffffff');
            }
        } else {
            this.timerText.setText('Ready');
            this.timerText.setColor('#ffffff');
        }
    }

    private updateOrderCards(state: KitchenState): void {
        // Track which orders are still present
        const activeIds = new Set<string>();

        let index = 0;
        if (state.orders) {
            state.orders.forEach((order: Order, id: string) => {
                activeIds.add(id);

                let card = this.cardContainers.get(id);
                if (!card) {
                    card = this.createOrderCard(order, index);
                    this.cardContainers.set(id, card);
                    this.container.add(card);
                }

                // Update position (orders can shift as others expire)
                card.setPosition(CARD_START_X + index * (CARD_WIDTH + CARD_MARGIN), CARD_START_Y);

                // Update timer bar
                this.updateCardTimerBar(card, order);

                index++;
            });
        }

        // Remove cards for expired orders
        for (const [id, card] of this.cardContainers) {
            if (!activeIds.has(id)) {
                card.destroy();
                this.cardContainers.delete(id);
            }
        }
    }

    private createOrderCard(order: Order, index: number): Phaser.GameObjects.Container {
        const x = CARD_START_X + index * (CARD_WIDTH + CARD_MARGIN);
        const y = CARD_START_Y;
        const card = this.scene.add.container(x, y);

        // Background
        const bg = this.scene.add.graphics();
        bg.fillStyle(CARD_BG_COLOR, 0.85);
        bg.fillRoundedRect(0, 0, CARD_WIDTH, CARD_HEIGHT, 4);
        bg.lineStyle(CARD_BORDER_WIDTH, CARD_BORDER_COLOR, 1);
        bg.strokeRoundedRect(0, 0, CARD_WIDTH, CARD_HEIGHT, 4);
        card.add(bg);

        // Dish name
        const dishName = DISH_NAMES[order.dishType] || order.dishType.replace(/_/g, ' ');
        const nameText = this.scene.add.text(CARD_PADDING, CARD_PADDING, dishName, {
            fontSize: '11px', color: '#fff', fontStyle: 'bold'
        });
        card.add(nameText);

        // Recipe steps (compact, single line — no wordWrap to prevent overflow beyond the HUD band)
        const steps = RECIPE_STEPS.get(order.dishType as IngredientTypes);
        if (steps) {
            const stepLabels = buildStepLabelsString(steps);
            const stepsText = this.scene.add.text(CARD_PADDING, CARD_PADDING + 16, stepLabels, {
                fontSize: '8px', color: '#aaa',
                fixedWidth: STEP_LABELS_FIXED_WIDTH, fixedHeight: 10
            });
            card.add(stepsText);
        }

        // Reward
        const rewardText = this.scene.add.text(CARD_WIDTH - CARD_PADDING, CARD_PADDING, `+${order.reward}`, {
            fontSize: '10px', color: '#ffcc00', fontStyle: 'bold'
        }).setOrigin(1, 0);
        card.add(rewardText);

        // Timer bar (updated each frame via updateCardTimerBar)
        const timerBar = this.scene.add.graphics();
        timerBar.setName('timerBar');
        card.add(timerBar);

        return card;
    }

    private updateCardTimerBar(card: Phaser.GameObjects.Container, order: Order): void {
        const timerBar = card.getByName('timerBar') as Phaser.GameObjects.Graphics;
        if (!timerBar) return;

        timerBar.clear();

        const fraction = Math.max(0, order.timeRemaining / ORDER_TIMEOUT_MS);
        const barWidth = Math.floor((CARD_WIDTH - CARD_PADDING * 2) * fraction);

        // Color based on urgency; at critical threshold, flash by toggling visibility
        let color: number;
        if (fraction <= URGENCY_CRITICAL) {
            // Flash red: toggle visibility at FLASH_INTERVAL_MS intervals
            const flashOn = Math.floor(Date.now() / FLASH_INTERVAL_MS) % 2 === 0;
            color = 0xFF3333;
            timerBar.setVisible(flashOn);
        } else if (fraction <= URGENCY_HIGH) {
            color = 0xFF3333; // red
            timerBar.setVisible(true);
        } else if (fraction <= URGENCY_MEDIUM) {
            color = 0xFFAA00; // orange/yellow
            timerBar.setVisible(true);
        } else {
            color = 0x33CC33; // green
            timerBar.setVisible(true);
        }

        // Background bar (dark)
        timerBar.fillStyle(0x333333, 0.8);
        timerBar.fillRect(CARD_PADDING, TIMER_BAR_Y_OFFSET, CARD_WIDTH - CARD_PADDING * 2, TIMER_BAR_HEIGHT);

        // Remaining time bar
        timerBar.fillStyle(color, 1);
        timerBar.fillRect(CARD_PADDING, TIMER_BAR_Y_OFFSET, barWidth, TIMER_BAR_HEIGHT);
    }

    getContainer(): Phaser.GameObjects.Container {
        return this.container;
    }

    destroy(): void {
        this.container.destroy();
        this.cardContainers.clear();
    }
}
